import { useEffect } from "react";
import { useSelector } from "react-redux";
import i18n from "../i18n";
import config from "../../config";
import { http } from "../lib";
import {
    resolveLanguageCode,
    i18nKeyForLanguage
} from "../lib/userLanguage";
import { logToSentry } from "../../config/Sentry";

const STORAGE_KEY = "cvControllerLang";

/**
 * Sync User Language
 * @description The API returns data (e.g. status names) in the language held on
 * the user's UserLanguage record, but the Controller only knows the language
 * saved in this browser. Read the API value once per session and align the
 * browser with it, reloading once if the screen language has to change.
 * Does nothing if the user has no saved language or the request fails.
 */
export default () => {
    const userId = useSelector((state) => state.auth?.account?.id);

    useEffect(() => {
        if (!userId) return;
        const syncedKey = `cvLangSynced:${userId}`;
        try {
            // Set before reloading so a mismatch can never cause a reload loop
            if (sessionStorage.getItem(syncedKey)) return;
            sessionStorage.setItem(syncedKey, "1");
        } catch (error) {
            return; // storage blocked: skip rather than risk reloading repeatedly
        }

        const sync = async () => {
            try {
                const { data } = await http.authorized.get(
                    `${config.api.paths.userLanguage}?user=${userId}`
                );
                const rows = Array.isArray(data) ? data : data?.results;
                const code = resolveLanguageCode(rows?.[0]?.language_code);
                if (!code) return;

                localStorage.setItem(STORAGE_KEY, code);
                if (i18n.language !== i18nKeyForLanguage(code)) {
                    window.location.reload();
                }
            } catch (error) {
                logToSentry("On user language sync:", { error });
            }
        };
        sync();
    }, [userId]);
};
